/**
 * shared/pmove/pmove.ts — BO2 (T6 MP) player movement: Pmove / PmoveSingle and the ground/air move.
 *
 * OWNER: PMOVE agent.
 *
 * Port of research/pmove-t6mp.md [PM] (T6 decompile: Pmove 0x48a040, PmoveSingle 0x947ed0,
 * PM_Friction 0x940a20, PM_Accelerate 0x940c80, PM_CmdScale_Walk 0x943fb0, PM_CmdScale_Air 0x941160,
 * PM_WalkMove 0x9475a0, PM_AirMove 0x9472c0), with the body of anything the T6 pseudocode condenses
 * taken from the KisakBlack T5 MP reimplementation [T5 bg_pmove.cpp] (research/movement-public.md
 * §0: T6 is a direct evolution of it; every T6 constant we have matches).
 *
 * Frame order (T6 PmoveSingle, PM §2 / §8.3):
 *   UpdateViewAngles -> flags (ADS / walking) -> PM_UpdateSprint -> PM_CheckDuck -> GroundTrace
 *   -> Mantle_Check (mantling: Mantle_Move + PM_Weapon, done) -> prone pitch -> DropTimers
 *   -> ladder / melee-charge checks -> Ladder | MeleeCharge | Walk | Air | Swim -> GroundTrace
 *   -> Footsteps -> PM_Weapon (ctx.weapons.step) -> "moved less than half" fix-up -> truncate v.
 *
 * Determinism: only + - * / sqrt floor trunc and the shared d* trig. pmove() ends with
 * quantizePlayerState (f32 / int wrap), so server, worker and browser get identical bits.
 */
import { CONTENTS, MASK_PLAYERSOLID } from '../collision/types';
import {
  ENTITYNUM_NONE, PM_ACCEL_CROUCH, PM_ACCEL_PRONE, PM_ACCEL_SLICK, PM_ACCEL_STAND, PM_ADS_MOVE_SCALE,
  PM_AIR_ACCEL, PM_SPRINT_SPEED_SCALE, PM_SPRINT_STRAFE_SCALE, PMOVE_MAX_CATCHUP_MS,
  PMOVE_MAX_CHUNK_MS, PMOVE_MSEC_MAX, PMOVE_MSEC_MIN,
} from '../constants';
import { SURF } from '../collision/types';
import {
  angleToShort, angleVectors, shortToAngle, snapVectorTrunc, vnormalize, type Vec3,
} from '../math';
import { quantizePlayerState } from '../playerstate';
import {
  Button, PMF, PmType, WeaponState, copyUserCmd, type PlayerState, type UserCmd,
} from '../types';
import { checkDuck, checkProne, cmdScaleForStance, setStanceHull, updatePronePitch } from './stance';
import { dtpCanMove, dtpCheckStart, dtpReduceFriction, dtpUpdate } from './dtp';
import { footsteps } from './footsteps';
import { groundTrace, unstick } from './ground';
import { checkJump, jumpApplySlowdown, jumpClearState, jumpReduceFriction } from './jump';
import { checkLadderMove, clearLadderFlag, ladderMove } from './ladder';
import { mantleCapView, mantleCheck, mantleMove } from './mantle';
import { checkMeleeCharge, meleeChargeMove } from './melee';
import { clipVelocity, projectVelocity, stepSlideMove } from './slide';
import { updateSprint } from './sprint';
import {
  angleDelta, angleNorm180, angleNorm360, createPm, effectiveStance, PM_LADDER_YAWCAP,
  PM_PRONE_TURN_RATE, PM_PRONE_YAWCAP, PM_STANCE_CROUCH, PM_STANCE_PRONE, PRONE_FEET_DIST_TURNED,
  resetPml, vec2Normalize, vecToYaw, type Pm,
} from './pm';
import type { PmoveContext } from './types';

// =============================================================================================
// Pmove (0x48a040)
// =============================================================================================
let pmCache: Pm | null = null;
function getPm(ps: PlayerState, ctx: PmoveContext): Pm {
  if (!pmCache) pmCache = createPm(ctx, ps);
  pmCache.ps = ps;
  pmCache.ctx = ctx;
  return pmCache;
}

/**
 * Pmove (0x48a040): run one usercmd. Splits into <= 66 ms PmoveSingle chunks, with at most 150 ms
 * of catch-up [PM §2]. Each chunk sees a fresh copy of the cmd (pmove's edits to pm->cmd, e.g. the
 * sprint strafe scale, never compound across chunks); oldButtons = the previous cmd as sent.
 */
export function pmove(ps: PlayerState, cmd: Readonly<UserCmd>, ctx: PmoveContext): void {
  ctx.touchEnts.length = 0;
  if (ps.commandTime > cmd.serverTime) return; // old / duplicate cmd
  if (cmd.serverTime - ps.commandTime > PMOVE_MAX_CATCHUP_MS) ps.commandTime = cmd.serverTime - PMOVE_MAX_CATCHUP_MS;
  const pm = getPm(ps, ctx);
  pm.xyspeed = 0;
  while (ps.commandTime !== cmd.serverTime) {
    const msec = Math.min(cmd.serverTime - ps.commandTime, PMOVE_MAX_CHUNK_MS);
    copyUserCmd(pm.cmd, cmd as UserCmd);
    pm.cmd.serverTime = ps.commandTime + msec;
    pm.oldButtons = ps.oldButtons;
    pmoveSingle(pm);
    ps.oldButtons = cmd.buttons;
  }
  quantizePlayerState(ps);
}

// =============================================================================================
// PM_UpdateViewAngles (0x5f0c20 / 0x51f530) [T5 PM_UpdateViewAngles + _Clamp/_Prone/_LadderClamp]
// =============================================================================================
/** Make cmd.angles + deltaAngles reproduce `deg` on `axis` next frame (deltaAngles are shorts). */
function forceViewAngle(ps: PlayerState, cmd: Readonly<UserCmd>, axis: number, deg: number): void {
  ps.viewangles[axis] = deg;
  ps.deltaAngles[axis] = (((angleToShort(deg) - cmd.angles[axis]) << 16) >> 16);
}

/** viewangles = SHORT2ANGLE(cmd.angles + deltaAngles), pitch clamped to player_view_pitch_up/down. */
export function updateViewAngles(ps: PlayerState, cmd: Readonly<UserCmd>, ctx: PmoveContext): void {
  const pm = getPm(ps, ctx);
  if (pm.cmd !== cmd) copyUserCmd(pm.cmd, cmd as UserCmd);
  pmUpdateViewAngles(pm, 0);
}

function pmUpdateViewAngles(pm: Pm, msec: number): void {
  const ps = pm.ps;
  const cmd = pm.cmd;
  if (ps.pm_type === PmType.INTERMISSION) return;
  const oldViewYaw = ps.viewangles[1];
  for (let i = 0; i < 3; i++) {
    const s = (cmd.angles[i] + ps.deltaAngles[i]) & 0xffff;
    let a = shortToAngle(s);
    if (a > 180) a -= 360;
    ps.viewangles[i] = a;
  }
  // pitch clamp (player_view_pitch_up/down 85, T5 bg_misc.cpp:587; ESTIMATE for T6)
  const upLim = -pm.ctx.dvars.pitchUpLimit, downLim = pm.ctx.dvars.pitchDownLimit;
  if (ps.viewangles[0] < upLim || ps.viewangles[0] > downLim) {
    forceViewAngle(ps, cmd, 0, ps.viewangles[0] < upLim ? upLim : downLim);
  }
  if (ps.pm_type >= PmType.LASTSTAND && ps.pm_type !== PmType.FREEZE) return;
  if (ps.pm_flags & PMF.MANTLE) {
    if (mantleCapView(ps) !== 0) forceViewAngle(ps, cmd, 1, angleNorm180(ps.viewangles[1]));
    return;
  }
  if ((ps.pm_flags & PMF.LADDER) && ps.groundEntityNum === ENTITYNUM_NONE) {
    // PM_UpdateViewAngles_LadderClamp, bg_ladder_yawcap 100
    const facing = vecToYaw(ps.vLadderVec) + 180;
    const d = angleDelta(facing, ps.viewangles[1]);
    if (d > PM_LADDER_YAWCAP || d < -PM_LADDER_YAWCAP) {
      forceViewAngle(ps, cmd, 1, angleNorm180(d > 0 ? facing - PM_LADDER_YAWCAP : facing + PM_LADDER_YAWCAP));
    }
  }
  if (ps.pm_flags & PMF.PRONE) updateViewAnglesProne(pm, msec, oldViewYaw);
}

/**
 * PM_UpdateViewAngles_Prone [T5 bg_pmove.cpp:1097], simplified: the body turns toward the view at
 * 55 deg/s when the view leaves +-(45-5) or you move, if BG_CheckProne allows the new heading;
 * then the view is clamped to the body +-45 (bg_prone_yawcap) and pitch to the torso +-45.
 * (The T5 one-degree retry loops are not ported.)
 */
function updateViewAnglesProne(pm: Pm, msec: number, _oldViewYaw: number): void {
  const ps = pm.ps;
  const cmd = pm.cmd;
  const viewYaw = ps.viewangles[1];
  const delta = angleDelta(ps.proneDirection, viewYaw);
  const moving = (cmd.forwardmove !== 0 || cmd.rightmove !== 0) && delta !== 0;
  if (msec > 0 && (delta > PM_PRONE_YAWCAP - 5 || delta < -(PM_PRONE_YAWCAP - 5) || moving)) {
    const maxDeltaYaw = PM_PRONE_TURN_RATE * msec * 0.001;
    const newProneYaw = Math.abs(delta) >= maxDeltaYaw
      ? (delta > 0 ? ps.proneDirection - maxDeltaYaw : ps.proneDirection + maxDeltaYaw)
      : viewYaw;
    if (checkProne(pm, ps.origin, 15, 30, newProneYaw, true, ps.groundEntityNum !== ENTITYNUM_NONE, true, PRONE_FEET_DIST_TURNED)) {
      ps.proneDirection = angleNorm360(newProneYaw);
    }
  }
  const d = angleDelta(ps.proneDirection, ps.viewangles[1]);
  if (d > PM_PRONE_YAWCAP || d < -PM_PRONE_YAWCAP) {
    forceViewAngle(ps, cmd, 1, angleNorm180(d > 0 ? ps.proneDirection - PM_PRONE_YAWCAP : ps.proneDirection + PM_PRONE_YAWCAP));
  }
  const dp = angleDelta(ps.proneTorsoPitch, ps.viewangles[0]);
  if (dp > 45 || dp < -45) forceViewAngle(ps, cmd, 0, angleNorm180(dp > 0 ? ps.proneTorsoPitch - 45 : ps.proneTorsoPitch + 45));
}

// =============================================================================================
// Flags: sight aiming (0x10) and the ADS walking flag (0x40)
// =============================================================================================
/**
 * PM_UpdateAimDownSightFlag + PM_IsAdsAllowed [T5 bg_weapons.cpp:472]: pm_flags 0x10 (sight
 * aiming; ends/blocks sprint) while ADS is held and allowed. Pressing sprint drops ADS.
 */
function updateAimDownSightFlag(pm: Pm): void {
  const ps = pm.ps;
  ps.pm_flags &= ~PMF.UNK_0x10;
  if (!(pm.cmd.buttons & Button.ADS) || (pm.cmd.buttons & Button.SPRINT)) return;
  if (ps.pm_type !== PmType.NORMAL && ps.pm_type !== PmType.NORMAL_LINKED) return;
  if (!pm.ctx.weaponDef(ps.weapon).aimDownSight) return;
  if (ps.waterlevel >= 3) return;
  const ws = ps.weaponState;
  if (ws === WeaponState.RAISING || ws === WeaponState.RAISING_ALTSWITCH || ws === WeaponState.DROPPING
    || ws === WeaponState.DROPPING_QUICK || ws === WeaponState.FIRST_RAISE
    || (ws >= WeaponState.OFFHAND_INIT && ws <= WeaponState.OFFHAND_END)) return;
  if (ps.pm_flags & PMF.MANTLE) return;
  // prone: ADS can only be raised while not crawling (then crawling is blocked, see pmoveSingle)
  if ((ps.pm_flags & PMF.PRONE) && (pm.oldButtons & Button.ADS) && (pm.cmd.forwardmove || pm.cmd.rightmove)) return;
  ps.pm_flags |= PMF.UNK_0x10;
}

/**
 * PM_UpdatePlayerWalkingFlag [T5 bg_pmove.cpp:5166]: pm_flags 0x40 (x0.4 move scale, ADS move
 * speed) while ADS is held, sight-aiming, not prone and not reloading.
 */
function updatePlayerWalkingFlag(pm: Pm): void {
  const ps = pm.ps;
  ps.pm_flags &= ~PMF.ADS_SLOW;
  if (ps.pm_type >= PmType.FREEZE) return;
  if (!(pm.cmd.buttons & Button.ADS)) return;
  if (ps.pm_flags & PMF.PRONE) return;
  if (!(ps.pm_flags & PMF.UNK_0x10)) return;
  const ws = ps.weaponState;
  if (ws >= WeaponState.RELOADING && ws <= WeaponState.RELOAD_END) return;
  ps.pm_flags |= PMF.ADS_SLOW;
}

// =============================================================================================
// Friction / Accelerate / CmdScale
// =============================================================================================
const fricVec: Vec3 = [0, 0, 0];
/** Water friction factor per waterlevel (T6 table {0,1,2,2} with level 2 = 1.7, PM §3) ESTIMATE. */
const WATER_FRICTION = [0, 1, 1.7, 2, 2, 2];

/** PM_Friction (0x940a20) [PM §3]. */
export function friction(pm: Pm): void {
  const ps = pm.ps;
  const vel = ps.velocity;
  fricVec[0] = vel[0]; fricVec[1] = vel[1]; fricVec[2] = pm.walking ? 0 : vel[2];
  const speed = Math.sqrt(fricVec[0] * fricVec[0] + fricVec[1] * fricVec[1] + fricVec[2] * fricVec[2]);
  if (speed < 1) { // 1.0 @0xc25a04
    vel[0] = 0; vel[1] = 0; vel[2] = 0;
    return;
  }
  const d = pm.ctx.dvars;
  let drop = 0;
  if (ps.pm_flags & PMF.SPECIAL_FRICTION) {
    if (ps.meleeChargeTime > 0) drop = (speed / (ps.meleeChargeTime * 0.001)) * pm.frametime; // melee charge decay
  } else if (ps.waterlevel <= 1) {
    const slick = (pm.groundTrace.surfaceFlags & SURF.SLICK) !== 0;
    if (pm.walking && !(ps.pm_flags & (PMF.SLIDE | PMF.TIME_KNOCKBACK)) && !slick) {
      let control = speed < d.stopspeed ? d.stopspeed : speed;
      if (ps.pm_flags & PMF.TIME_SLOWDOWN) control *= 0.30000001; // @0xc172ac
      else if (ps.pm_flags & PMF.JUMPING) control *= jumpReduceFriction(pm); // PM_GetJumpLandScale
      if (ps.pm_flags & PMF.DIVE) control *= dtpReduceFriction(ps);
      drop += control * d.friction * pm.frametime; // friction 5.5
    }
    if (slick) drop += speed * 1.5 * pm.frametime; // slick friction: hidden 0x322ea00, T5 player_sliding_friction 1.5 ESTIMATE
  }
  if (ps.waterlevel !== 0 && pm.walking) drop += speed * WATER_FRICTION[Math.min(5, ps.waterlevel)] * pm.frametime;
  if (ps.pm_type === PmType.SPECTATOR) drop += speed * 5 * pm.frametime; // @0xc8bcd4
  let newspeed = speed - drop;
  if (newspeed < 0) newspeed = 0;
  const k = newspeed / speed;
  vel[0] *= k; vel[1] *= k; vel[2] *= k;
}

const pushDir: Vec3 = [0, 0, 0];
/**
 * PM_Accelerate (0x940c80) [PM §3]: IW form, the accel uses max(wishspeed, stopspeed) as its floor.
 * On a ladder: accelerate straight toward the wish velocity.
 */
export function accelerate(pm: Pm, wishdir: Vec3, wishspeed: number, accel: number): void {
  const ps = pm.ps;
  const v = ps.velocity;
  if (ps.pm_flags & PMF.LADDER) {
    pushDir[0] = wishspeed * wishdir[0] - v[0];
    pushDir[1] = wishspeed * wishdir[1] - v[1];
    pushDir[2] = wishspeed * wishdir[2] - v[2];
    const pushLen = vnormalize(pushDir);
    let canPush = accel * pm.frametime * wishspeed;
    if (canPush > pushLen) canPush = pushLen;
    v[0] += canPush * pushDir[0]; v[1] += canPush * pushDir[1]; v[2] += canPush * pushDir[2];
    return;
  }
  const currentspeed = v[0] * wishdir[0] + v[1] * wishdir[1] + v[2] * wishdir[2];
  const addspeed = wishspeed - currentspeed;
  if (addspeed <= 0) return;
  const stopspeed = pm.ctx.dvars.stopspeed;
  let accelspeed = accel * pm.frametime * (wishspeed < stopspeed ? stopspeed : wishspeed);
  if (accelspeed > addspeed) accelspeed = addspeed;
  v[0] += accelspeed * wishdir[0]; v[1] += accelspeed * wishdir[1]; v[2] += accelspeed * wishdir[2];
}

/**
 * PM_CmdScale_Walk (0x943fb0) [PM §3]: ground wishspeed. Back 0.7 / strafe 0.8 (ESTIMATE dvars),
 * ADS/lean/prone-raised x0.4, sprint x1.5 standing only, stance 1/0.65/0.15 (blended), water,
 * weapon part (ctx.weapons.moveSpeedScale: moveSpeedScale, or the ADS one), script multiplier.
 * Analog input survives: speed * max(|f|,|r|) / (127 * |(f,r)|); diagonals are not faster.
 */
export function cmdScaleWalk(pm: Pm): number {
  const ps = pm.ps;
  const d = pm.ctx.dvars;
  const fwd = pm.cmd.forwardmove, rt = pm.cmd.rightmove;
  const total = Math.sqrt(fwd * fwd + rt * rt);
  const fAbs = fwd < 0 ? Math.abs(fwd * d.backSpeedScale) : Math.abs(fwd);
  const rAbs = Math.abs(rt * d.strafeSpeedScale);
  const m = fAbs > rAbs ? fAbs : rAbs;
  if (m === 0) return 0;
  let scale = (ps.speed * m) / (127 * total);
  if ((ps.pm_flags & PMF.ADS_SLOW) || ps.leanf !== 0 || ((ps.pm_flags & PMF.PRONE) && ps.adsFrac > 0)) {
    scale *= PM_ADS_MOVE_SCALE; // @0xbd13d8
  }
  if ((ps.pm_flags & PMF.SPRINTING) && ps.viewHeightTarget === d.standingViewHeight) scale *= PM_SPRINT_SPEED_SCALE;
  scale *= cmdScaleForStance(pm);
  if (ps.waterlevel >= 1) scale *= 1 - ps.waterlevel * (1 / 3) * 0.4; // @0xc57734, @0xbfa4bc
  if (ps.weapon) scale *= pm.ctx.weapons.moveSpeedScale(ps);
  return scale * ps.moveSpeedScaleMultiplier;
}

/** PM_CmdScale_Air (0x941160) [PM §3]: no back/strafe/sprint/stance/weapon scaling in the air. */
export function cmdScaleAir(pm: Pm): number {
  const ps = pm.ps;
  const fwd = pm.cmd.forwardmove, rt = pm.cmd.rightmove;
  const af = Math.abs(fwd), ar = Math.abs(rt);
  const m = af > ar ? af : ar;
  if (m === 0) return 0;
  let scale = (ps.speed * m) / (127 * Math.sqrt(fwd * fwd + rt * rt));
  if ((ps.pm_flags & PMF.ADS_SLOW) || ps.leanf !== 0 || (ps.pm_flags & PMF.DIVE)) scale *= PM_ADS_MOVE_SCALE;
  if (ps.pm_type === PmType.NOCLIP) scale *= 3;
  else if (ps.pm_type === PmType.UFO) scale *= 6;
  return scale;
}

/** PM_DamageSlowdown (0x6154c0) [PM §3]: 1 - A*t/B (A = dmgTimerMinScale ESTIMATE 0 -> no slowdown). */
function damageScaleWalk(pm: Pm, damageTimer: number): number {
  if (!damageTimer) return 1;
  const d = pm.ctx.dvars;
  if (d.dmgTimerMinScale === 0 || d.dmgTimerMaxTime === 0) return 1;
  return 1 - (d.dmgTimerMinScale * damageTimer) / d.dmgTimerMaxTime;
}

// =============================================================================================
// Walk / Air / Swim / Fly / Noclip / Dead
// =============================================================================================
const wishvel: Vec3 = [0, 0, 0];
const wishdir: Vec3 = [0, 0, 0];

/** PM_WalkMove (0x9475a0) [PM §3]. */
export function walkMove(pm: Pm): void {
  const ps = pm.ps;
  if (ps.pm_flags & PMF.JUMPING) jumpApplySlowdown(pm);
  if (ps.pm_flags & PMF.SPRINTING) pm.cmd.rightmove = Math.trunc(pm.cmd.rightmove * PM_SPRINT_STRAFE_SCALE); // (char)(rm*0.667)
  if (checkJump(pm) || dtpCheckStart(pm)) {
    airMove(pm);
    return;
  }
  friction(pm);
  const fmove = pm.cmd.forwardmove, smove = pm.cmd.rightmove;
  const scale = cmdScaleWalk(pm) * damageScaleWalk(pm, ps.damageTimer);
  ps.damageTimer -= Math.trunc(pm.frametime * 1000);
  if (ps.damageTimer <= 0) ps.damageTimer = 0;
  pm.forward[2] = 0; pm.right[2] = 0;
  vec2Normalize(pm.forward);
  vec2Normalize(pm.right);
  wishvel[0] = fmove * pm.forward[0] + smove * pm.right[0];
  wishvel[1] = fmove * pm.forward[1] + smove * pm.right[1];
  wishvel[2] = 0;
  let wishspeed = vnormalize(wishdir, wishvel);
  wishspeed *= scale;
  projectVelocity(wishdir, pm.groundTrace.normal, wishdir);
  const iStance = effectiveStance(ps);
  let accel: number;
  if ((pm.groundTrace.surfaceFlags & SURF.SLICK) || (ps.pm_flags & PMF.SLIDE)) accel = PM_ACCEL_SLICK;
  else if (ps.pm_flags & PMF.TIME_KNOCKBACK) accel = PM_AIR_ACCEL;
  else if (iStance === PM_STANCE_PRONE) accel = PM_ACCEL_PRONE;
  else if (iStance === PM_STANCE_CROUCH) accel = PM_ACCEL_CROUCH;
  else accel = PM_ACCEL_STAND; // standing, incl. sprint
  if (ps.pm_flags & PMF.TIME_SLOWDOWN) accel *= 0.25;
  accelerate(pm, wishdir, wishspeed, accel);
  if ((pm.groundTrace.surfaceFlags & SURF.SLICK) || (ps.pm_flags & (PMF.SLIDE | PMF.TIME_KNOCKBACK))) {
    ps.velocity[2] -= ps.gravity * pm.frametime;
  }
  projectVelocity(ps.velocity, pm.groundTrace.normal, ps.velocity);
  stepSlideMove(pm, false);
  setMovementDir(pm);
}

/** PM_AirMove (0x9472c0) [PM §3]: air accel 1, clip against a steep ground plane, gravity slide. */
export function airMove(pm: Pm): void {
  const ps = pm.ps;
  friction(pm);
  const fmove = pm.cmd.forwardmove, smove = pm.cmd.rightmove;
  const scale = cmdScaleAir(pm);
  pm.forward[2] = 0; pm.right[2] = 0;
  vnormalize(pm.forward);
  vnormalize(pm.right);
  wishvel[0] = pm.forward[0] * fmove + pm.right[0] * smove;
  wishvel[1] = pm.forward[1] * fmove + pm.right[1] * smove;
  wishvel[2] = 0;
  let wishspeed = vnormalize(wishdir, wishvel);
  wishspeed *= scale;
  accelerate(pm, wishdir, wishspeed, PM_AIR_ACCEL);
  if (pm.groundPlane) clipVelocity(ps.velocity, pm.groundTrace.normal, ps.velocity); // overbounce 1.001
  stepSlideMove(pm, true);
  setMovementDir(pm);
}

/** PM_SwimMove [T5 bg_pmove.cpp:2799] (no undertow / float). Hijacked's ocean kills, so this is rarely used. */
function swimMove(pm: Pm): void {
  const ps = pm.ps;
  friction(pm);
  const fmove = pm.cmd.forwardmove, smove = pm.cmd.rightmove;
  const scale = cmdScaleAir(pm) * 1.3; // player_waterSpeedScale 1.3 [T5 bg_misc.cpp] ESTIMATE
  for (let i = 0; i < 3; i++) wishvel[i] = pm.forward[i] * fmove + pm.right[i] * smove;
  if (ps.waterlevel === 3 && wishvel[2] >= 0) wishvel[2] = 0;
  let wishspeed = vnormalize(wishdir, wishvel);
  wishspeed *= scale;
  accelerate(pm, wishdir, wishspeed, 2); // pm_wateraccelerate
  pm.groundPlane = false;
  pm.walking = false;
  stepSlideMove(pm, false);
  setMovementDir(pm);
}

/** PM_FlyMove [T5 bg_pmove.cpp:2401] (spectator). */
function flyMove(pm: Pm): void {
  const ps = pm.ps;
  friction(pm);
  const scale = cmdScaleAir(pm);
  if (scale === 0) {
    wishvel[0] = wishvel[1] = wishvel[2] = 0;
  } else {
    // forward = up x right
    const r = pm.right;
    const fx = -r[1], fy = r[0], fz = 0;
    wishvel[0] = (pm.cmd.forwardmove * fx + pm.cmd.rightmove * r[0]) * scale;
    wishvel[1] = (pm.cmd.forwardmove * fy + pm.cmd.rightmove * r[1]) * scale;
    wishvel[2] = (pm.cmd.forwardmove * fz + pm.cmd.rightmove * r[2]) * scale;
  }
  if (ps.speed) {
    const up = ps.speed; // PM_MoveScale(0,0,127)
    if (pm.cmd.buttons & Button.CROUCH) wishvel[2] -= up;
    if (pm.cmd.buttons & Button.JUMP) wishvel[2] += up;
  }
  const wishspeed = vnormalize(wishdir, wishvel);
  accelerate(pm, wishdir, wishspeed, 8); // pm_flyaccelerate
  stepSlideMove(pm, false);
}

/** PM_NoclipMove (Q3 lineage): free flight, no collision. pm_type NOCLIP (x3) / UFO (x6). */
function noclipMove(pm: Pm): void {
  const ps = pm.ps;
  const v = ps.velocity;
  const speed = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  if (speed < 1) {
    v[0] = v[1] = v[2] = 0;
  } else {
    const drop = (speed < pm.ctx.dvars.stopspeed ? pm.ctx.dvars.stopspeed : speed) * pm.ctx.dvars.friction * 1.5 * pm.frametime;
    const k = Math.max(0, speed - drop) / speed;
    v[0] *= k; v[1] *= k; v[2] *= k;
  }
  for (let i = 0; i < 3; i++) wishvel[i] = pm.forward[i] * pm.cmd.forwardmove + pm.right[i] * pm.cmd.rightmove;
  if (pm.cmd.buttons & Button.JUMP) wishvel[2] += 127;
  if (pm.cmd.buttons & Button.CROUCH) wishvel[2] -= 127;
  const len = vnormalize(wishdir, wishvel);
  const wishspeed = Math.min(len, 127) * ((ps.speed * (ps.pm_type === PmType.UFO ? 6 : 3)) / 127); // x3 noclip, x6 ufo
  accelerate(pm, wishdir, wishspeed, PM_ACCEL_STAND);
  ps.origin[0] += pm.frametime * v[0]; ps.origin[1] += pm.frametime * v[1]; ps.origin[2] += pm.frametime * v[2];
}

/** PM_DeadMove [T5 bg_pmove.cpp:3056]: extra friction when dead on the ground. */
function deadMove(pm: Pm): void {
  if (!pm.walking) return;
  const v = pm.ps.velocity;
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  const f = len - 20;
  if (f <= 0) { v[0] = v[1] = v[2] = 0; return; }
  const k = f / len;
  v[0] *= k; v[1] *= k; v[2] *= k;
}

const moved: Vec3 = [0, 0, 0];
/**
 * PM_SetMovementDir (0x941220) [T5 bg_pmove.cpp:2726]: move yaw relative to the view, clamped
 * to +-90 (backwards runs mirrored), stored as i8 in ps.movementDir.
 */
export function setMovementDir(pm: Pm): void {
  const ps = pm.ps;
  let moveyaw = 0;
  if (ps.pm_flags & PMF.PRONE) {
    moveyaw = Math.trunc(angleDelta(ps.proneDirection, ps.viewangles[1]));
  } else if (ps.pm_flags & PMF.LADDER) {
    moveyaw = Math.trunc(angleDelta(vecToYaw(ps.vLadderVec) + 180, ps.viewangles[1]));
  } else {
    moved[0] = ps.origin[0] - pm.previousOrigin[0];
    moved[1] = ps.origin[1] - pm.previousOrigin[1];
    moved[2] = ps.origin[2] - pm.previousOrigin[2];
    const speed = Math.sqrt(moved[0] * moved[0] + moved[1] * moved[1] + moved[2] * moved[2]);
    if ((pm.cmd.forwardmove || pm.cmd.rightmove) && ps.groundEntityNum !== ENTITYNUM_NONE && speed !== 0 && speed > pm.frametime * 5) {
      moveyaw = Math.trunc(angleDelta(vecToYaw(moved), ps.viewangles[1]));
      if (pm.cmd.forwardmove < 0) moveyaw = Math.trunc(angleNorm180(moveyaw + 180));
    }
  }
  if (moveyaw > 90) moveyaw = 90;
  else if (moveyaw < -90) moveyaw = -90;
  ps.movementDir = moveyaw & 0xff;
}

// =============================================================================================
// Timers / water
// =============================================================================================
/** PM_DropTimers [T5 bg_pmove.cpp:5121]: pm_time counts down; at 0 the timed flags clear. */
function dropTimers(pm: Pm): void {
  const ps = pm.ps;
  if (!ps.pm_time) return;
  if (pm.msec < ps.pm_time) {
    ps.pm_time -= pm.msec;
  } else {
    if (ps.pm_flags & PMF.JUMPING) jumpClearState(ps);
    ps.pm_flags &= ~(PMF.TIME_SLOWDOWN | PMF.TIME_KNOCKBACK | PMF.JUMPING | PMF.DIVE); // & 0xFFDFBE7F
    ps.pm_time = 0;
  }
}

const wpt: Vec3 = [0, 0, 0];
/** PM_SetWaterLevel / PM_GetWaterLevel [T5 bg_pmove.cpp:580]: water at feet+3 / +24 / +45 / +54 / +60. */
function setWaterLevel(pm: Pm): void {
  const ps = pm.ps;
  wpt[0] = ps.origin[0]; wpt[1] = ps.origin[1];
  const heights = [3, 24, 45, 54, 60];
  let level = 0;
  for (let i = 0; i < heights.length; i++) {
    wpt[2] = ps.origin[2] + heights[i];
    if (!(pm.ctx.pointContents(wpt, ps.clientNum) & CONTENTS.WATER)) break;
    level = i + 1;
  }
  ps.waterlevel = level;
}

// =============================================================================================
// PmoveSingle (0x947ed0)
// =============================================================================================
/** Buttons kept while frozen (prematch / PMF_FROZEN): stance only [T5 PmoveSingle bitarray(12, 8, 9)]. */
const FROZEN_BUTTON_MASK = Button.CROUCH | Button.PRONE | Button.SCOREBOARD;

function pmoveSingle(pm: Pm): void {
  const ps = pm.ps;
  const cmd = pm.cmd;
  resetPml(pm);
  setStanceHull(pm); // set_stance
  pm.tracemask = MASK_PLAYERSOLID;
  if (ps.pm_flags & PMF.SPECIAL_FRICTION) { // melee lunge drives you forward
    cmd.forwardmove = 127;
    cmd.rightmove = 0;
  }
  if ((ps.pm_flags & (PMF.FROZEN | PMF.UNK_0x800)) || ps.pm_type === PmType.FREEZE) {
    cmd.buttons &= FROZEN_BUTTON_MASK;
    cmd.forwardmove = 0;
    cmd.rightmove = 0;
    ps.velocity[0] = 0; ps.velocity[1] = 0; ps.velocity[2] = 0;
  }
  if (ps.pm_type >= PmType.DEAD && ps.pm_type !== PmType.FREEZE) pm.tracemask &= ~CONTENTS.BODY;
  let msec = cmd.serverTime - ps.commandTime;
  msec = msec < PMOVE_MSEC_MIN ? PMOVE_MSEC_MIN : msec > PMOVE_MSEC_MAX ? PMOVE_MSEC_MAX : msec;
  pm.msec = msec;
  ps.commandTime = cmd.serverTime;
  pm.previousOrigin[0] = ps.origin[0]; pm.previousOrigin[1] = ps.origin[1]; pm.previousOrigin[2] = ps.origin[2];
  pm.previousVelocity[0] = ps.velocity[0]; pm.previousVelocity[1] = ps.velocity[1]; pm.previousVelocity[2] = ps.velocity[2];
  pm.frametime = msec * 0.001; // @0xc44e34
  pmUpdateViewAngles(pm, msec);
  setWaterLevel(pm);
  angleVectors(ps.viewangles, pm.forward, pm.right, pm.up);
  if (cmd.forwardmove < 0) ps.pm_flags |= PMF.BACKWARDS_RUN;
  else if (cmd.forwardmove > 0 || cmd.rightmove) ps.pm_flags &= ~PMF.BACKWARDS_RUN;
  if (ps.pm_type >= PmType.DEAD && ps.pm_type !== PmType.FREEZE) { cmd.forwardmove = 0; cmd.rightmove = 0; }
  // aiming down the sight while prone: no crawling [T5 PmoveSingle: pm_flags 0x10 && stance prone]
  if ((ps.pm_flags & PMF.UNK_0x10) && effectiveStance(ps) === PM_STANCE_PRONE) { cmd.forwardmove = 0; cmd.rightmove = 0; }
  if (!dtpCanMove(ps)) { cmd.forwardmove = 0; cmd.rightmove = 0; }

  switch (ps.pm_type) {
    case PmType.NOCLIP:
    case PmType.UFO:
      ps.groundEntityNum = ENTITYNUM_NONE;
      clearLadderFlag(ps);
      dropTimers(pm);
      noclipMove(pm);
      snapVectorTrunc(ps.velocity);
      return;
    case PmType.SPECTATOR:
      clearLadderFlag(ps);
      checkDuck(pm);
      dropTimers(pm);
      flyMove(pm);
      snapVectorTrunc(ps.velocity);
      return;
    case PmType.INTERMISSION:
      clearLadderFlag(ps);
      return;
    default:
      break;
  }
  if (ps.pm_type === PmType.LASTSTAND || ps.pm_type === PmType.DEAD) clearLadderFlag(ps);

  if (!(ps.pm_flags & PMF.MANTLE)) {
    unstick(pm); // T6 solver un-penetration (approximation, see ground.ts)
    updateAimDownSightFlag(pm);
    updateSprint(pm);
    updatePlayerWalkingFlag(pm);
    checkDuck(pm);
    groundTrace(pm);
  }
  mantleCheck(pm);
  if (ps.pm_flags & PMF.MANTLE) {
    clearLadderFlag(ps);
    ps.groundEntityNum = ENTITYNUM_NONE;
    pm.groundPlane = false;
    pm.walking = false;
    updateAimDownSightFlag(pm);
    updateSprint(pm);
    updatePlayerWalkingFlag(pm);
    checkDuck(pm);
    mantleMove(pm);
    pm.ctx.weapons.step(ps, cmd, msec, pm.ctx);
    return; // T5/T6: the mantle path returns before the fix-up and the velocity truncation
  }
  updatePronePitch(pm);
  dropTimers(pm);
  if (ps.pm_type >= PmType.LASTSTAND && ps.pm_type !== PmType.FREEZE) deadMove(pm);
  checkLadderMove(pm);
  checkMeleeCharge(pm);
  dtpUpdate(pm);
  if (ps.pm_flags & PMF.LADDER) ladderMove(pm);
  else if (ps.pm_flags & PMF.SPECIAL_FRICTION) meleeChargeMove(pm);
  else if (ps.waterlevel < 3) {
    if (pm.walking) walkMove(pm);
    else airMove(pm);
  } else swimMove(pm);
  groundTrace(pm);
  footsteps(pm);
  pm.ctx.weapons.step(ps, cmd, msec, pm.ctx);
  // moved less than half the intended distance: use the real displacement as velocity (0.25 @0xbda7c8)
  const mx = ps.origin[0] - pm.previousOrigin[0];
  const my = ps.origin[1] - pm.previousOrigin[1];
  const mz = ps.origin[2] - pm.previousOrigin[2];
  const v = ps.velocity;
  const ft2 = pm.frametime * pm.frametime;
  if (0.25 * (v[0] * v[0] + v[1] * v[1] + v[2] * v[2]) > (mx * mx + my * my + mz * mz) / ft2) {
    const inv = 1 / pm.frametime;
    v[0] = inv * mx; v[1] = inv * my; v[2] = inv * mz;
  }
  snapVectorTrunc(v); // 0x502120: truncate toward zero every PmoveSingle
}

/** Exposed for tests / tools: the pm scratch used by the last pmove call (read-only use). */
export function lastPm(): Pm | null {
  return pmCache;
}
